import { ButtonSizeEnum, ButtonTypeEnum } from "@/components/atoms/Button";
import {
  BookADemoSourceEnum,
  CtaActionType,
  CtaType,
  SignUpSourceEnum,
} from "@/components/molecules/CtaButtonGroup";
import { GraphyHeadPropType } from "@/components/organisms/GraphyHead";
import { LANDING_PAGE_EVENTS } from "@/constants/amplitude.constants";
import { getCDNURL } from "@/shared/utils/commons";
import {
  BOOK_A_DEMO_en,
  GET_STARTED_en,
} from "@/shared/data/common.data";

/**
 * Home V2 data — India (en) locale only.
 * This is the data contract for the Figma-driven redesign at
 * src/components/Home/v2. It is intentionally isolated from
 * the legacy HomePageDataType (src/shared/data/home.data.ts)
 * so that us/kr locales remain unaffected.
 */

export type HeroStatProps = {
  value: string;
  label: string;
};

export type CreatorAvatarProps = {
  src: string;
  alt: string;
  name: string;
  role: string;
};

export type HeroV2Props = {
  /** First line of the headline (grey / muted). */
  titleMuted: string;
  /** Second line of the headline (rendered with an indigo→magenta gradient). */
  titleBright: string;
  description: string;
  ctas: CtaType[];
  stats: HeroStatProps[];
  creatorAvatars: CreatorAvatarProps[];
};

export type PlatformTabVisualKind =
  | "course-video"
  | "membership-plans"
  | "community-chat"
  | "coaching"
  | "webinar-grid"
  | "digital-downloads"
  | "ai-avatar-chat"
  | "coming-soon";

export type PlatformTabItem = {
  id: string;
  label: string;
  icon: string; // FontAwesome class (inline with label)
  title: string; // H3 inside the featured card
  description: string; // paragraph inside the featured card
  ctaLabels: { primary: string; secondary: string };
  learnMoreHref?: string;
  visualKind: PlatformTabVisualKind;
  comingSoon?: boolean;
  image?: string;
  imageMobile?: string;
};

export type PlatformTabsProps = {
  eyebrow?: string;
  titleMuted: string; // grey portion of the title
  titleBright: string; // white portion
  subtitle?: string;
  primaryCta: CtaType;
  secondaryCta: CtaType;
  tabs: PlatformTabItem[];
};

export type IdeaToIncomeCard = {
  /** Small pink uppercase label rendered above the title (e.g. "AI WEBSITE BUILDER"). */
  eyebrow?: string;
  title: string;
  description: string;
  image?: string;
  imageMobile?: string;
  accent?: "pink" | "blue" | "green";
};

export type IdeaToIncomeProps = {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  cards: IdeaToIncomeCard[];
  /** Optional CTA pair rendered between the header and the card grid
   *  (used on product pages — e.g. "LAUNCH / Sell Globally" — where the
   *  CTAs need to sit above the cards). */
  ctas?: CtaType[];
  /** Grid columns on md+ viewports. Defaults to 2 (HomeV2 uses 2).
   *  Product pages use 3 for a denser "Flexible Pricing / Sell Worldwide /
   *  Taxes Automated" layout. */
  columns?: 2 | 3;
};

export type AiTeamCardKind =
  | "ai-operator"
  | "ai-agents"
  | "ai-avatar";

export type AiTeamCard = {
  kind: AiTeamCardKind;
  badge: string; // "Launch 80% faster", "Convert 5X more visitors", etc.
  title: string;
  description: string;
  /** Optional illustration. When set, replaces the hardcoded mockup with
   *  this image inside the gradient card. */
  image?: string;
};

export type AiTeamProps = {
  eyebrow?: string;
  titleMuted: string;
  titleBright: string;
  subtitle?: string;
  ctas: CtaType[];
  cards: AiTeamCard[];
  image?: string;
  imageMobile?: string;
};

/**
 * A single "How It Works" bullet. Each entry renders as an icon + text
 * row in the expanded accordion. `icon` is an identifier (string) that
 * AutomationsSection maps to a FontAwesome IconDefinition at render
 * time — keeping FA imports out of this data file.
 */
export type AutomationBullet = {
  /** Identifier looked up in AutomationsSection → IconDefinition map. */
  icon: string;
  text: string;
};

export type AutomationFeature = {
  id: string;
  title: string;
  description?: string;
  bullets?: AutomationBullet[];
  /** Header above the bullet list. Defaults to "How It Works:". Pass ""
   *  to suppress the label entirely. */
  bulletsHeading?: string;
  /** Optional muted line rendered directly under the bullet list. Used
   *  for closing one-liners like "Flexible rules, zero headaches." */
  footerLine?: string;
  /** Path to the image rendered on the right column when this feature
   *  is active. Container dimensions are fixed across all features so
   *  swapping doesn't cause layout shift. */
  image?: string;
  imageMobile?: string;
};

export type AutomationsProps = {
  eyebrow?: string;
  title?: string;
  subtitle?: string;
  features: AutomationFeature[];
  /** Where the image sits relative to the accordion. Defaults to "right".
   *  Per-section override lets the Product V2 "SELL" panel flip to image-
   *  left while the HomeV2 "Automations" panel stays image-right. */
  imagePosition?: "left" | "right";
  /** Optional CTA pair rendered below the subtitle in the section header. */
  ctas?: CtaType[];
};

export type CreatorsGridProps = {
  eyebrow?: string;
  /** Rendered as first line (grey/muted) when present alongside titleBright. */
  titleMuted?: string;
  /** Rendered as second line (gradient) when present alongside titleMuted. */
  titleBright?: string;
  /** Fallback single-string title (used when titleMuted/titleBright not provided). */
  title?: string;
  highlightedWord?: string;
  subtitle?: string;
  /** CTA pair rendered under the subtitle (Start Free Trial + Book A Demo). */
  ctas?: CtaType[];
  /** Single collage image shown below the CTAs. */
  image?: string;
  /** Mobile-specific collage image; falls back to `image` when unset. */
  imageMobile?: string;
};

export type FeatureTile = {
  /** Font Awesome class string, e.g. "fa-regular fa-globe". */
  icon: string;
  /** Hex color applied to the icon only (no pill background). */
  iconColor?: string;
  title: string;
  description: string;
  /** If set, the card renders as a Next.js <Link> to this path. */
  href?: string;
};

export type FeaturesGridV2Props = {
  eyebrow?: string;
  title: string;
  highlightedWord?: string;
  subtitle?: string;
  tiles: FeatureTile[];
};

export type CtaBannerV2Props = {
  titleMuted: string;
  titleBright: string;
  description?: string;
  ctas: CtaType[];
  backgroundImage: string;
  backgroundImageMobile?: string;
};

export type FooterV2SocialLink = {
  kind: "youtube" | "instagram" | "linkedin" | "facebook" | "twitter" | "mail";
  href: string;
  label: string;
};

export type FooterV2LinkGroup = {
  heading: string;
  links: { label: string; href: string }[];
};

export type FooterV2Props = {
  tagline: string;
  description: string;
  socialLinks: FooterV2SocialLink[];
  copyright: string;
  linkGroups: FooterV2LinkGroup[];
  wordmark: string; // "GRAPHY" text used for the top-left brand lockup
  /** Path to the big faded "GRAPHY" SVG that bleeds off the bottom of
   *  the section. When absent, the slot renders empty (but still
   *  reserves vertical space). */
  wordmarkImage?: string;
};

export type HomeV2DataType = {
  seoData: GraphyHeadPropType;
  hero: HeroV2Props;
  platformTabs: PlatformTabsProps;
  ideaToIncome: IdeaToIncomeProps;
  aiTeam: AiTeamProps;
  automations: AutomationsProps;
  creatorsGrid: CreatorsGridProps;
  featuresGrid: FeaturesGridV2Props;
  ctaBanner: CtaBannerV2Props;
  footer: FooterV2Props;
};

const seoData: GraphyHeadPropType = {
  title: "Graphy - Build & Sell Courses, Memberships, Digital Products and More",
  description:
    "Trusted by 200000+ creators & businesses to build, sell and scale courses, memberships, digital products and more. Start for free.",
  canonicalPath: "",
  countryCode: "",
};

// On the new design the "Start Free Trial" button is the white
// pill (Secondary in our enum) and "Book A Demo" is the indigo pill
// (Primary). The wording/colors are swapped vs. the legacy Primary=SignUp
// convention, hence the slightly confusing enum assignment below.
const primaryCta: CtaType = {
  type: ButtonTypeEnum.Secondary,
  size: ButtonSizeEnum.Regular,
  text: "Start Free Trial",
  actionType: CtaActionType.SignUp,
  amplitudeEvent: {
    eventName: LANDING_PAGE_EVENTS.SIGN_UP_TAPPED.LABEL,
    eventProperties: {
      source: SignUpSourceEnum.hero,
    },
  },
};

const platformPrimaryCta: CtaType = {
  type: ButtonTypeEnum.Primary,
  size: ButtonSizeEnum.Regular,
  text: "Start Free Trial",
  actionType: CtaActionType.SignUp,
  amplitudeEvent: {
    eventName: LANDING_PAGE_EVENTS.SIGN_UP_TAPPED.LABEL,
    eventProperties: {
      source: SignUpSourceEnum.products_section,
    },
  },
};

const platformSecondaryCta: CtaType = {
  type: ButtonTypeEnum.Secondary,
  size: ButtonSizeEnum.Regular,
  text: "Learn More",
  actionType: CtaActionType.NoAction,
};

const secondaryCta: CtaType = {
  type: ButtonTypeEnum.Primary,
  size: ButtonSizeEnum.Regular,
  text: "Book A Demo",
  actionType: CtaActionType.BookDemo,
  amplitudeEvent: {
    eventName: LANDING_PAGE_EVENTS.BOOK_DEMO_TAPPED.LABEL,
    eventProperties: {
      source: BookADemoSourceEnum.hero,
    },
  },
};

export const homeV2Data: HomeV2DataType = {
  seoData,
  hero: {
    titleMuted: "Your Creator Business",
    titleBright: "Faster and Smarter with AI",
    description:
      "Create courses, memberships, communities, coaching, and digital products. Launch your website and mobile app instantly. Sell and market automatically with intelligent AI.",
    ctas: [primaryCta, secondaryCta],
    stats: [
      { value: "200,000+", label: "Creators Worldwide" },
      { value: "₹1,500 Crore+", label: "Creator Revenue" },
      { value: "4.8/5", label: "Rating" },
      { value: "150+", label: "Countries Served" },
    ],
    // Sourced from the India Creators website image set (8 headshots).
    // Files live in /public/images/Home Page/creators/ — served via CDN.
    creatorAvatars: [
      {
        src: `${getCDNURL()}graphy-web/V4/images/Home Page/creators/Ankit Awasthi (UPSC Educator).png`,
        alt: "Ankit Awasthi",
        name: "Ankit Awasthi",
        role: "Finance Educator",
      },
      {
        src: `${getCDNURL()}graphy-web/V4/images/Home Page/creators/Ankur Warikoo (Personal & Financial Coach).png`,
        alt: "Ankur Warikoo",
        name: "Ankur Warikoo",
        role: "Personal & Financial Coach",
      },
      {
        src: `${getCDNURL()}graphy-web/V4/images/Home Page/creators/Atish Mathur (UPSC Educator).png`,
        alt: "Atish Mathur",
        name: "Atish Mathur",
        role: "UPSC Educator",
      },
      {
        src: `${getCDNURL()}graphy-web/V4/images/Home Page/creators/Damini Tripathi (Marketing Strategist).png`,
        alt: "Damini Tripathi",
        name: "Damini Tripathi",
        role: "Marketing Strategist",
      },
      {
        src: `${getCDNURL()}graphy-web/V4/images/Home Page/creators/Priya Mohit Gadhiya (Skill Educator).png`,
        alt: "Priya Mohit Gadhiya",
        name: "Priya Mohit Gadhiya",
        role: "Skill Educator",
      },
      {
        src: `${getCDNURL()}graphy-web/V4/images/Home Page/creators/Ravi Prakash (MBA Educator).png`,
        alt: "Ravi Prakash",
        name: "Ravi Prakash",
        role: "MBA Educator",
      },
      {
        src: `${getCDNURL()}graphy-web/V4/images/Home Page/creators/Sagar Sinha (Financial Educator).png`,
        alt: "Sagar Sinha",
        name: "Sagar Sinha",
        role: "Financial Educator",
      },
      {
        src: `${getCDNURL()}graphy-web/V4/images/Home Page/creators/Sr. Subodh Chaturvedi (Medical Educator).png`,
        alt: "Sr. Subodh Chaturvedi",
        name: "Sr. Subodh Chaturvedi",
        role: "Medical Educator",
      },
    ],
  },
  platformTabs: {
    eyebrow: "CREATE",
    titleMuted: "Everything Your Audience Needs,",
    titleBright: "In One Platform",
    subtitle:
      "From first course to a full-scale academy—create unlimited products, reach wider audience, and build multiple income streams.",
    primaryCta: platformPrimaryCta,
    secondaryCta: platformSecondaryCta,
    tabs: [
      {
        id: "courses",
        label: "Courses",
        icon: "fa-solid fa-book-open",
        title: "Online Courses: Build Comprehensive Learning Experiences",
        description:
          "Run self-paced or cohort-based programs with video, audio, documents, and quizzes — while enabling students' progress tracking and certificates as they complete their learning.",
        ctaLabels: { primary: "Start Free Trial", secondary: "Learn More" },
        learnMoreHref: "/create-and-sell-course-online",
        visualKind: "course-video",
        image: `${getCDNURL()}graphy-web/V4/images/Home Page/Features Section/Courses.png`,
      },
      {
        id: "memberships",
        label: "Memberships",
        icon: "fa-solid fa-calendar",
        title: "Memberships: Create Recurring Revenue Streams",
        description:
          "Offer premium content, exclusive community access, and ongoing value that keeps members paying month after month.",
        ctaLabels: { primary: "Start Free Trial", secondary: "Learn More" },
        learnMoreHref: "/launch-membership",
        visualKind: "membership-plans",
        image: `${getCDNURL()}graphy-web/V4/images/Home Page/Features Section/Memberships.png`,
      },
      {
        id: "communities",
        label: "Communities",
        icon: "fa-solid fa-users",
        title: "Communities: Build Engaged Learning Tribes",
        description:
          "Create either free or paid communities with discussion forums, peer networking, and direct connections — giving your students a space to support each other and stay engaged.",
        ctaLabels: { primary: "Start Free Trial", secondary: "Learn More" },
        learnMoreHref: "/community-chat-for-creators",
        visualKind: "community-chat",
        image: `${getCDNURL()}graphy-web/V4/images/Home Page/Features Section/Communities.png`,
      },
      {
        id: "coaching",
        label: "Coaching",
        icon: "fa-solid fa-user-headset",
        title: "1:1 Coaching: Scale Your One-On-One Expertise",
        description:
          "Deliver group coaching or private sessions — all supported by built-in scheduling, video calls, and progress tracking.",
        ctaLabels: { primary: "Start Free Trial", secondary: "Learn More" },
        learnMoreHref: "/sell-coaching-programs",
        visualKind: "coaching",
        image: `${getCDNURL()}graphy-web/V4/images/Home Page/Features Section/Coaching.png`,
      },
      {
        id: "webinars",
        label: "Webinars",
        icon: "fa-solid fa-video",
        title: "Webinars: Turn Your Knowledge Into Scalable Experiences",
        description:
          "Host free or paid webinars — complete with booking, video calls, payments, and engagement tools that help learners actually progress.",
        ctaLabels: { primary: "Start Free Trial", secondary: "Learn More" },
        learnMoreHref: "/conduct-live-webinars",
        visualKind: "webinar-grid",
        image: `${getCDNURL()}graphy-web/V4/images/Home Page/Features Section/Webinars.png`,
      },
      {
        id: "digital-products",
        label: "Digital Products",
        icon: "fa-solid fa-file",
        title: "Digital Products: Sell Downloads Instantly",
        description:
          "Sell ebooks, templates, worksheets, text, links, videos, or other resources — with files delivered automatically after purchase.",
        ctaLabels: { primary: "Start Free Trial", secondary: "Learn More" },
        learnMoreHref: "/sell-digital-products",
        visualKind: "digital-downloads",
        image: `${getCDNURL()}graphy-web/V4/images/Home Page/Features Section/Digital Products.png`,
      },
      {
        id: "ai-avatars",
        label: "AI Avatars",
        icon: "fa-solid fa-wand-magic-sparkles",
        title: "AI Avatar: Your Digital Teaching Assistant",
        description:
          "Create an AI version of yourself that answers questions, provides guidance, and gives learners access to your teaching 24/7.",
        ctaLabels: { primary: "Start Free Trial", secondary: "Learn More" },
        learnMoreHref: "/ai-avatar",
        visualKind: "ai-avatar-chat",
        image: `${getCDNURL()}graphy-web/V4/images/Home Page/Features Section/AI Avatars.png`,
      },
    ],
  },
  ideaToIncome: {
    eyebrow: "LAUNCH",
    title: "From Idea to Income in No Time",
    subtitle:
      "Everything you need to launch and sell — no code, no tech team, no delays.",
    cards: [
      {
        eyebrow: "AI WEBSITE BUILDER",
        title: "Your Professional Website - Built By AI",
        description:
          "Tell our AI your vision, and get a complete, conversion-optimized website in minutes.",
        accent: "pink",
        image: `${getCDNURL()}graphy-web/V4/images/Home Page/Launch Section/AI website builder.png`,
      },
      {
        eyebrow: "BRANDED MOBILE APP",
        title: "Your Own Mobile App",
        description:
          "Every Graphy business includes a beautiful mobile app.",
        accent: "blue",
        image: `${getCDNURL()}graphy-web/V4/images/Home Page/Launch Section/branded mobile app.png`,
      },
    ],
  },
  aiTeam: {
    eyebrow: "SCALE",
    titleMuted: "Your AI Team that",
    titleBright: "Builds, Sells & Scales",
    subtitle:
      "Other platforms give you tools. Graphy gives you intelligence. Every part of your business learns, improves, and works harder over time—so you can succeed faster with less effort.",
    ctas: [
      {
        type: ButtonTypeEnum.Secondary,
        size: ButtonSizeEnum.Regular,
        text: "Start Free Trial",
        actionType: CtaActionType.SignUp,
        amplitudeEvent: {
          eventName: LANDING_PAGE_EVENTS.SIGN_UP_TAPPED.LABEL,
          eventProperties: { source: SignUpSourceEnum.body_end },
        },
      },
      {
        type: ButtonTypeEnum.Primary,
        size: ButtonSizeEnum.Regular,
        text: "Book A Demo",
        actionType: CtaActionType.BookDemo,
        amplitudeEvent: {
          eventName: LANDING_PAGE_EVENTS.BOOK_DEMO_TAPPED.LABEL,
          eventProperties: { source: BookADemoSourceEnum.bodyEnd },
        },
      },
    ],
    cards: [
      {
        kind: "ai-operator",
        badge: "Launch 80% faster",
        title: "AI Operator",
        description: "Build your entire teaching business in hours",
        image: `${getCDNURL()}graphy-web/V4/images/Home Page/Scale Section/AI Operator.png`,
      },
      {
        kind: "ai-agents",
        badge: "Convert 5X more visitors",
        title: "AI Agents",
        description: "Sales, support, and marketing—handled instantly",
        image: `${getCDNURL()}graphy-web/V4/images/Home Page/Scale Section/AI Agents.png`,
      },
      {
        kind: "ai-avatar",
        badge: "Monetize 1:1 interactions",
        title: "AI Avatar",
        description: "Your audience gets personal tutoring, 24x7",
        image: `${getCDNURL()}graphy-web/V4/images/Home Page/Scale Section/AI Avatar.png`,
      },
    ],
  },
  automations: {
    eyebrow: "SELL",
    title: "Build Once. Sell on repeat.\nWith Intelligent Automations",
    subtitle:
      "Most creators lose 70% of potential sales because they can't respond fast enough. Graphy's AI engages every visitor, closes sales around the clock, and markets your business while you sleep.",
    features: [
      {
        id: "never-miss-a-sale",
        title: "Never Miss A Sale",
        description:
          "Your website automatically engages visitors, answers product questions, recommends the perfect course, and guides them to purchase—24 hours a day, 7 days a week.",
        bullets: [
          { icon: "question", text: 'Answers "Which course is right for me?"' },
          { icon: "tag", text: "Explains pricing & curriculum details" },
          { icon: "clipboard", text: "Handles enrollment questions" },
          { icon: "moon", text: "Closes sales while you sleep" },
        ],
        image: `${getCDNURL()}graphy-web/V4/images/Home Page/Sell Section/Never Miss A Sale.png`,
      },
      {
        id: "support-that-scales",
        title: "Support That Scales Infinitely",
        description:
          "Students get accurate answers to any question—about content, technical issues, or enrollment—without waiting for you. Your AI Agent is trained on your entire course library.",
        bulletsHeading: "What it handles:",
        bullets: [
          { icon: "key", text: "Course content questions" },
          { icon: "headset", text: "Technical troubleshooting" },
          { icon: "receipt", text: "Access & enrollment issues" },
          { icon: "globe", text: "Instant, accurate responses 24/7" },
        ],
        image: `${getCDNURL()}graphy-web/V4/images/Home Page/Sell Section/Support That Scales Infinitely.png`,
      },
      {
        id: "grow-your-audience",
        title: "Grow Your Audience On Autopilot",
        description:
          "Welcome sequences, promotional emails, and re-engagement—all set up and optimized automatically.",
        bulletsHeading: "What it handles:",
        bullets: [
          { icon: "envelope", text: "Email and WhatsApp campaign setup" },
          { icon: "window", text: "Writing and publishing SEO blogs" },
          { icon: "share", text: "Creating campaign copies" },
          { icon: "chart", text: "Continuous optimization based on performance feedback" },
        ],
        image: `${getCDNURL()}graphy-web/V4/images/Home Page/Sell Section/Grow your audience on autopilot.png`,
      },
      {
        id: "analytics",
        title: "Analytics That Tell You What Works",
        description:
          "Track every visitor, every sale, every student action. See which marketing works, which courses sell best, and where to focus your energy.",
        bulletsHeading: "What it handles:",
        bullets: [
          { icon: "chart", text: "Revenue by product & source" },
          { icon: "graduation", text: "Student engagement metrics" },
          { icon: "flag", text: "Marketing campaign performance" },
          { icon: "lightbulb", text: "Conversion funnel analysis" },
        ],
        image: `${getCDNURL()}graphy-web/V4/images/Home Page/Sell Section/Analytics That Tell You What Works.png`,
      },
    ],
  },
  creatorsGrid: {
    eyebrow: "STORIES",
    titleMuted: "Real Creators",
    titleBright: "Real Revenue",
    subtitle:
      "From first-time creators to established educators—Graphy helps them launch faster, earn more, and work less.",
    ctas: [
      {
        type: ButtonTypeEnum.Secondary,
        size: ButtonSizeEnum.Regular,
        text: "Start Free Trial",
        actionType: CtaActionType.SignUp,
        amplitudeEvent: {
          eventName: LANDING_PAGE_EVENTS.SIGN_UP_TAPPED.LABEL,
          eventProperties: { source: SignUpSourceEnum.body_end },
        },
      },
      {
        type: ButtonTypeEnum.Primary,
        size: ButtonSizeEnum.Regular,
        text: "Book A Demo",
        actionType: CtaActionType.BookDemo,
        amplitudeEvent: {
          eventName: LANDING_PAGE_EVENTS.BOOK_DEMO_TAPPED.LABEL,
          eventProperties: { source: BookADemoSourceEnum.bodyEnd },
        },
      },
    ],
    image: `${getCDNURL()}graphy-web/V4/images/Home Page/Stories Section/Creator Images.png`,
  },
  featuresGrid: {
    eyebrow: "FEATURES",
    title: "Features That Level Up Your Business",
    highlightedWord: "Level Up",
    subtitle:
      "One platform. One login. One simple dashboard. No juggling 10 different tools or paying for 5 subscriptions.",
    tiles: [
      {
        icon: "fa-regular fa-globe",
        iconColor: "#CA26A5",
        title: "AI Website Builder",
        description: "Instant, conversion-ready website built by AI.",
        href: "/ai-website-builder",
      },
      {
        icon: "fa-regular fa-infinity",
        iconColor: "#B3741A",
        title: "AI Agent",
        description:
          "Always-on AI that sells, supports, & engages users.",
        href: "/ai-agent",
      },
      {
        icon: "fa-regular fa-mobile-screen",
        iconColor: "#EF64F2",
        title: "Mobile App",
        description:
          "Branded app for learning, engagement, and retention.",
        href: "/no-code-mobile-apps-for-creators",
      },
      {
        icon: "fa-regular fa-money-bill-1",
        iconColor: "#4F7F06",
        title: "Integrated Payments",
        description:
          "Global payments and subscriptions - fully handled.",
        href: "/accept-payments-for-creators",
      },
      {
        icon: "fa-regular fa-brain",
        iconColor: "#B92B39",
        title: "AI Brain",
        description:
          "Graphy’s central knowledge base that powers AI responses using your content.",
        href: "/ai-brain",
      },
      {
        icon: "fa-regular fa-megaphone",
        iconColor: "#007F8C",
        title: "Marketing Tools",
        description:
          "Funnels, emails, and campaigns - ready to convert.",
        href: "/marketing-tools-for-creators",
      },
      {
        icon: "fa-regular fa-shield",
        iconColor: "#877102",
        title: "Security",
        description:
          "DRM-protected content with enterprise-grade security.",
        href: "/secure-your-content",
      },
      {
        icon: "fa-regular fa-link",
        iconColor: "#007F8C",
        title: "Third-Party Support",
        description: "Seamless integrations with tools you already use.",
        href: "/integrations-in-graphy",
      },
    ],
  },
  ctaBanner: {
    titleMuted: "Ready to Build",
    titleBright: "Your Creator Business?",
    description:
      "Launch faster, sell more, & work less with intelligent automation. Join 200,000+ creators who chose the smarter way.",
    ctas: [
      {
        type: ButtonTypeEnum.Secondary,
        size: ButtonSizeEnum.Regular,
        text: "Start Free Trial",
        actionType: CtaActionType.SignUp,
        amplitudeEvent: {
          eventName: LANDING_PAGE_EVENTS.SIGN_UP_TAPPED.LABEL,
          eventProperties: { source: SignUpSourceEnum.body_end },
        },
      },
      {
        type: ButtonTypeEnum.Primary,
        size: ButtonSizeEnum.Regular,
        text: "Book A Demo",
        actionType: CtaActionType.BookDemo,
        amplitudeEvent: {
          eventName: LANDING_PAGE_EVENTS.BOOK_DEMO_TAPPED.LABEL,
          eventProperties: { source: BookADemoSourceEnum.bodyEnd },
        },
      },
    ],
    backgroundImage: `${getCDNURL()}graphy-web/V4/images/Home Page/CTA Section/Footer Banner.png`,
    backgroundImageMobile: `${getCDNURL()}graphy-web/V4/images/Mobile/Home Page/Footer Banner/Banner.png`,
  },
  footer: {
    tagline: "GRAPHY",
    description:
      "Graphy is an all-in-one platform built for creators, coaches, and educators to run their knowledge business from a single place. It brings together website creation, digital products, courses, communities, payments, and AI-powered tools into one unified system.",
    socialLinks: [
      { kind: "youtube", href: "https://www.youtube.com/@GraphyAcademy", label: "YouTube" },
      {
        kind: "instagram",
        href: "https://www.instagram.com/graphyapp/",
        label: "Instagram",
      },
      {
        kind: "linkedin",
        href: "https://www.linkedin.com/company/graphyapp/",
        label: "LinkedIn",
      },
      {
        kind: "facebook",
        href: "https://www.facebook.com/creatorsongraphy",
        label: "Facebook",
      },
      { kind: "twitter", href: "https://x.com/graphyapp?lang=en", label: "X" },
      { kind: "mail", href: "mailto:care@graphy.com", label: "Email" },
    ],
    copyright: "© 2026 Graphy Inc.",
    linkGroups: [
      {
        heading: "COMPANY",
        links: [
          { label: "User Guides", href: "/legal/user-guidelines" },
          { label: "Affiliate Terms", href: "/legal/affiliate" },
          { label: "Refund Policy", href: "/legal/refund-policy" },
          { label: "Take down policy", href: "/legal/takedown-policy" },
          { label: "Updates", href: "/updates" },
        ],
      },
      {
        heading: "LEGAL",
        links: [
          { label: "Terms of Service", href: "/legal/terms" },
          { label: "Privacy Policy", href: "/legal/privacy" },
          { label: "Data Processing Addendum", href: "/legal/addendum" },
          { label: "Cookies Policy", href: "/legal/cookies" },
          {
            label: "UK Privacy representation",
            href: "/legal/uk-privacy-representation",
          },
          { label: "GDPR representation", href: "/legal/gdpr-representation" },
        ],
      },
    ],
    wordmark: "GRAPHY",
    wordmarkImage: `${getCDNURL()}graphy-web/V4/images/Home Page/footer-wordmark.png`,
  },
};
